import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';
import type { StorageError } from '@supabase/storage-js';
import { getSupabaseClient } from './supabase';
import { markRequest } from '../lib/actionFailureSignals';
import type { ChatProfile } from './orderChat';
import { recordRealtimeEvent } from '../lib/realtimeTelemetry';
export {
  subscribeToUserPresence,
  updateUserPresence,
  updateUserPresenceForProfile,
  type UserPresence,
  type UserPresenceStatus,
} from './presence';

type ConversationScope = 'standard' | 'ai_review' | 'all';
type ClientOption = { supabase?: SupabaseClient; conversationScope?: ConversationScope };

export type ConversationRow = {
  conversation_id: string;
  conversation_type: 'event' | 'transfer' | 'direct' | 'group';
  entity_id?: string | null;
  title?: string | null;
  joined_at?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  last_message_at?: string | null;
  unread_count?: number;
  pinned_at?: string | null;
  pinned_by?: string | null;
};

export function isAiReviewConversation(conversation: Pick<ConversationRow, 'title'> | null | undefined): boolean {
  const title = String(conversation?.title || '').trim();
  return /^AI\s+(Review|Analysis)\b/i.test(title);
}

function filterConversationsByScope(conversations: ConversationRow[], scope: ConversationScope = 'standard'): ConversationRow[] {
  if (scope === 'all') return conversations;
  if (scope === 'ai_review') return conversations.filter(isAiReviewConversation);
  return conversations.filter((conv) => !isAiReviewConversation(conv));
}

export type ConversationParticipantRow = {
  participant_id: string;
  conversation_id: string;
  profile_id: string;
  joined_at: string;
  left_at?: string | null;
  muted_at?: string | null;
  profile?: ChatProfile | null;
};

export type ConversationMessageRow = {
  id: string;
  conversation_id: string;
  author_profile_id: string;
  author_display_name?: string | null;
  body: string;
  attachments: any[];
  mentions: any[];
  created_at: string;
  updated_at: string;
  author?: ChatProfile | null;
  pinned_at?: string | null;
  pinned_by?: string | null;
};

export type ConversationMentionStatusRow = {
  id: string;
  message_id: string;
  mentioned_profile_id: string;
  read_at: string | null;
  dismissed_at: string | null;
  profile?: ChatProfile | null;
};

export type ConversationReadReceiptRow = {
  id: string;
  message_id: string;
  reader_profile_id: string;
  read_at: string;
  reader?: ChatProfile | null;
};

const normaliseProfile = (row: any): ChatProfile | null => {
  if (!row || typeof row !== 'object') return null;
  const id = row.id ? String(row.id) : '';
  if (!id) return null;
  return {
    id,
    full_name: row.full_name ?? null,
    first_name: row.first_name ?? null,
    last_name: row.last_name ?? null,
    email: row.email ?? null,
    headshot_url: row.headshot_url ?? null,
  };
};

const unwrapProfile = (value: any): ChatProfile | null => {
  if (!value) return null;
  const source = Array.isArray(value) ? value[0] ?? null : value ?? null;
  return normaliseProfile(source);
};

const unreadLoads = new WeakMap<SupabaseClient, Map<string, { until: number; request: Promise<any> }>>();
const activeUnreadLoads = new Set<Map<string, { until: number; request: Promise<any> }>>();
export function invalidateUnreadReads(): void { activeUnreadLoads.forEach(loads => loads.clear()); }
if (typeof window !== 'undefined') window.addEventListener('portal:write-committed', invalidateUnreadReads);
async function sharedUnreadRead(client: SupabaseClient, profileId: string, markers: Record<string, string>) {
  let loads = unreadLoads.get(client);
  if (!loads) { loads = new Map(); unreadLoads.set(client, loads); activeUnreadLoads.add(loads); client.auth.onAuthStateChange(() => loads!.clear()); }
  const key = JSON.stringify([profileId, markers]);
  const existing = loads.get(key);
  for (const [oldKey, value] of loads) { if (value.until <= Date.now()) loads.delete(oldKey); }
  if (existing && existing.until > Date.now()) return existing.request;
  const request = (async () => {
    const rows: ConversationRow[] = [];
    let cursor: string | null = null;
    do {
      const result: any = await markRequest(client.rpc('get_chat_unread_summary', {
        p_local_read_markers: markers, p_after: cursor, p_limit: 250,
      }), 'read');
      if (result.error) return { data: null, error: result.error };
      const page = result.data;
      rows.push(...(page.rows ?? []));
      const next = page.nextCursor as string | null;
      if (next && cursor && next <= cursor) throw new Error('Chat page did not advance');
      cursor = next;
    } while (cursor);
    rows.sort((a: any, b: any) =>
      (Date.parse(b.last_message_at ?? '') || 0) - (Date.parse(a.last_message_at ?? '') || 0)
      || (Date.parse(b.updated_at ?? '') || 0) - (Date.parse(a.updated_at ?? '') || 0));
    return { data: rows, error: null };
  })();
  const shared = { until: Infinity, request }; loads.set(key, shared);
  const result = await request;
  if (result.error) loads.delete(key); else shared.until = Date.now() + 250;
  return result;
}

export async function fetchConversations(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: ConversationRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const markers: Record<string, string> = {};
  try {
    const prefix = 'chat:lastRead:' + profileId + ':';
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key?.startsWith(prefix)) continue;
      const value = localStorage.getItem(key);
      if (value && Number.isFinite(Date.parse(value))) markers[key.slice(prefix.length)] = value;
    }
  } catch { /* Server read receipts remain authoritative without browser storage. */ }
  const { data, error } = await sharedUnreadRead(supabase, profileId, markers);
  if (error) return { data: [], error };
  // Seed a new device once, then retain that baseline through later messages.
  // Server receipts remain the cross-device source of truth.
  try {
    for (const row of data ?? []) {
      const key = `chat:lastRead:${profileId}:${row.conversation_id}`;
      if (!localStorage.getItem(key) && row.read_marker_at) localStorage.setItem(key, row.read_marker_at);
    }
  } catch { /* Storage may be unavailable. */ }
  return { data: filterConversationsByScope((data ?? []) as ConversationRow[], options?.conversationScope), error: null };
}

export async function fetchConversationMessages(
  conversationId: string,
  options?: ClientOption,
): Promise<{ data: ConversationMessageRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_messages')
    .select(
      `id,conversation_id,author_profile_id,author_display_name,body,attachments,mentions,created_at,updated_at,pinned_at,pinned_by,
       author:author_profile_id(id,full_name,first_name,last_name,email,headshot_url)`,
    )
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
    .limit(200);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        author: unwrapProfile((row as any).author),
      }))
    : [];
  return { data: rows as ConversationMessageRow[], error: error ?? null };
}

export function subscribeConversationMessages(params: {
  conversationId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channelName = `conversation-chat:${params.conversationId}`;
  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'quote_chat_messages',
        filter: `conversation_id=eq.${params.conversationId}`,
      },
      (payload) => {
        recordRealtimeEvent({ source: 'conversation-chat', table: 'quote_chat_messages', eventType: payload.eventType });
        // Debug logging
        console.log('[chat] Real-time message event:', {
          eventType: payload.eventType,
          hasNew: !!payload.new,
          hasOld: !!payload.old,
          conversationId: params.conversationId,
        });
        params.onChange(payload);
      },
    )
    .subscribe((status) => {
      console.log('[chat] Subscription status:', status, 'for conversation:', params.conversationId);
    });
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function fetchConversationMentionStatuses(
  conversationId: string,
  options?: ClientOption,
): Promise<{ data: ConversationMentionStatusRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_mentions')
    .select(
      'id,message_id,mentioned_profile_id,read_at,dismissed_at,profile:mentioned_profile_id(id,full_name,first_name,last_name,email)',
    )
    .eq('conversation_id', conversationId);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        profile: unwrapProfile((row as any).profile),
      }))
    : [];
  return { data: rows as ConversationMentionStatusRow[], error: error ?? null };
}

export function subscribeConversationMentionStatuses(params: {
  conversationId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`conversation-mention-status:${params.conversationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'quote_chat_mentions',
        filter: `conversation_id=eq.${params.conversationId}`,
      },
      (payload) => {
        recordRealtimeEvent({ source: 'conversation-mentions', table: 'quote_chat_mentions', eventType: payload.eventType });
        params.onChange(payload);
      },
    )
    .subscribe();
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function fetchConversationReadReceipts(
  conversationId: string,
  options?: ClientOption,
): Promise<{ data: ConversationReadReceiptRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('quote_chat_message_reads')
    .select('id,message_id,reader_profile_id,read_at,reader:reader_profile_id(id,full_name,first_name,last_name,email)')
    .eq('conversation_id', conversationId);
  const rows = Array.isArray(data)
    ? (data as any[]).map((row) => ({
        ...(row as any),
        reader: unwrapProfile((row as any).reader),
      }))
    : [];
  return { data: rows as ConversationReadReceiptRow[], error: error ?? null };
}

export function subscribeConversationReadReceipts(params: {
  conversationId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  const supabase = params.supabase ?? getSupabaseClient();
  const channel = supabase
    .channel(`conversation-reads:${params.conversationId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'quote_chat_message_reads',
        filter: `conversation_id=eq.${params.conversationId}`,
      },
      (payload) => {
        recordRealtimeEvent({ source: 'conversation-reads', table: 'quote_chat_message_reads', eventType: payload.eventType });
        params.onChange(payload);
      },
    )
    .subscribe();
  return () => {
    try {
      supabase.removeChannel(channel);
    } catch {
      /* noop */
    }
  };
}

export async function createDirectMessage(
  otherProfileId: string,
  options?: ClientOption,
): Promise<{ data: ConversationRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('create_direct_message', {
    p_other_profile_id: otherProfileId,
  });
  if (error) return { data: null, error };
  if (!data) return { data: null, error: null };
  return {
    data: {
      conversation_id: String(data.conversation_id),
      conversation_type: data.conversation_type,
      entity_id: data.entity_id ? String(data.entity_id) : null,
      title: data.title ?? null,
      created_at: String(data.created_at),
      updated_at: String(data.updated_at),
      created_by: data.created_by ? String(data.created_by) : null,
    } as ConversationRow,
    error: null,
  };
}

export async function createGroupChat(
  name: string,
  participantIds: string[],
  options?: ClientOption,
): Promise<{ data: ConversationRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.rpc('create_group_chat', {
    p_name: name,
    p_participant_ids: participantIds,
  });
  if (error) return { data: null, error };
  if (!data) return { data: null, error: null };
  return {
    data: {
      conversation_id: String(data.conversation_id),
      conversation_type: data.conversation_type,
      entity_id: data.entity_id ? String(data.entity_id) : null,
      title: data.title ?? null,
      created_at: String(data.created_at),
      updated_at: String(data.updated_at),
      created_by: data.created_by ? String(data.created_by) : null,
    } as ConversationRow,
    error: null,
  };
}

export async function sendConversationMessage(
  payload: {
    conversationId: string;
    body: string;
    attachments: any[];
    mentions: Array<{ profile_id: string; display_name: string }>;
    authorDisplayName: string | null;
    userId: string | null;
  },
  options?: ClientOption,
): Promise<PostgrestError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.rpc('send_chat_message', {
    p_conversation_id: payload.conversationId,
    p_body: payload.body,
    p_mentions: payload.mentions,
    p_attachments: payload.attachments,
    p_author_display_name: payload.authorDisplayName,
    p_user_id: payload.userId,
  });
  return error ?? null;
}

export async function getOrCreateConversation(
  entityType: 'event' | 'transfer_order',
  entityId: string,
  options?: ClientOption,
): Promise<{ data: ConversationRow | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const conversationType = entityType === 'event' ? 'event' : 'transfer';
  const { data, error } = await supabase.rpc('get_or_create_conversation', {
    p_entity_type: conversationType,
    p_entity_id: entityId,
  });
  if (error) return { data: null, error };
  if (!data) return { data: null, error: null };
  return {
    data: {
      conversation_id: String(data.conversation_id),
      conversation_type: data.conversation_type,
      entity_id: data.entity_id ? String(data.entity_id) : null,
      title: data.title ?? null,
      created_at: String(data.created_at),
      updated_at: String(data.updated_at),
      created_by: data.created_by ? String(data.created_by) : null,
    } as ConversationRow,
    error: null,
  };
}

export async function fetchTransferChatMessages(
  transferId: string,
  options?: ClientOption,
): Promise<{ data: ConversationMessageRow[]; error: PostgrestError | null }> {
  const { data: convData, error: convError } = await getOrCreateConversation('transfer_order', transferId, options);
  if (convError || !convData) {
    return { data: [], error: convError };
  }
  return fetchConversationMessages(convData.conversation_id, options);
}

export function subscribeTransferChatMessages(params: {
  transferId: string;
  supabase?: SupabaseClient;
  onChange: (payload: any) => void;
}): () => void {
  let unsubscribe: (() => void) | null = null;
  let conversationId: string | null = null;

  (async () => {
    const { data: convData } = await getOrCreateConversation('transfer_order', params.transferId, {
      supabase: params.supabase,
    });
    if (convData) {
      conversationId = convData.conversation_id;
      unsubscribe = subscribeConversationMessages({
        conversationId: convData.conversation_id,
        supabase: params.supabase,
        onChange: params.onChange,
      });
    }
  })();

  return () => {
    if (unsubscribe) unsubscribe();
  };
}

export async function fetchConversationParticipants(
  conversationId: string,
  options?: ClientOption,
): Promise<{ data: ConversationParticipantRow[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  // Try with muted_at first; if that column is absent on older deployments,
  // retry and synthesize muted_at so the return type remains stable.
  const initial = await supabase
    .from('chat_participants')
    .select(
      'participant_id,conversation_id,profile_id,joined_at,left_at,muted_at,profile:profile_id(id,full_name,first_name,last_name,email,headshot_url)',
    )
    .eq('conversation_id', conversationId)
    .is('left_at', null)
    .order('joined_at', { ascending: true });

  let rawRows = initial.data as Array<Record<string, any>> | null;
  let error = initial.error;

  // If error is about missing muted_at column, retry without it.
  if (error && (error.code === '42703' || error.message?.includes('muted_at'))) {
    console.warn('[chat] muted_at column not found, retrying without it:', error.message);
    const retry = await supabase
      .from('chat_participants')
      .select(
        'participant_id,conversation_id,profile_id,joined_at,left_at,profile:profile_id(id,full_name,first_name,last_name,email,headshot_url)',
      )
      .eq('conversation_id', conversationId)
      .is('left_at', null)
      .order('joined_at', { ascending: true });
    rawRows = Array.isArray(retry.data)
      ? retry.data.map((row) => ({ ...row, muted_at: null }))
      : null;
    error = retry.error;
  }

  const rows = Array.isArray(rawRows)
    ? rawRows.map((row) => ({
        ...(row as any),
        muted_at: row.muted_at ?? null,
        profile: unwrapProfile((row as any).profile),
      }))
    : [];
  return { data: rows as ConversationParticipantRow[], error: error ?? null };
}

export async function searchMentionCandidates(
  term: string,
  options?: ClientOption,
): Promise<{ data: ChatProfile[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const like = `%${term}%`;
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email,headshot_url')
    .eq('is_archived', false)
    .or(`full_name.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like}`)
    .order('full_name', { ascending: true })
    .limit(7);
  return { data: (data as ChatProfile[]) || [], error: error ?? null };
}

/** A person to start a chat with: the chat profile plus their home site's name. */
export type ChatPerson = ChatProfile & { home_site?: string | null };

const CHAT_PERSON_COLUMNS = 'id,full_name,first_name,last_name,email,headshot_url,home_location:home_location_id(name,display_name)';

function toChatPerson(row: Record<string, any>): ChatPerson {
  const site = Array.isArray(row.home_location) ? row.home_location[0] : row.home_location;
  const { home_location: _site, ...profile } = row;
  return { ...(profile as ChatProfile), home_site: site ? (site.display_name || site.name || null) : null };
}

/** New message search: active people by name or email, with their home site. */
export async function searchChatPeople(
  term: string,
  options?: ClientOption & { excludeProfileId?: string | null },
): Promise<{ data: ChatPerson[]; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const like = `%${term.replace(/[%_,()]/g, ' ').trim()}%`;
  let query = supabase
    .from('user_profiles')
    .select(CHAT_PERSON_COLUMNS)
    .eq('is_archived', false)
    .or(`full_name.ilike.${like},first_name.ilike.${like},last_name.ilike.${like},email.ilike.${like}`)
    .order('full_name', { ascending: true })
    .limit(8);
  if (options?.excludeProfileId) query = query.neq('id', options.excludeProfileId);
  const { data, error } = await query;
  return { data: Array.isArray(data) ? (data as Record<string, any>[]).map(toChatPerson) : [], error: error ?? null };
}

/** The people this person has direct chats with, most recently active first, each with that chat's id. */
export async function fetchRecentDirectPartners(
  profileId: string,
  options?: ClientOption & { limit?: number },
): Promise<{ data: Array<{ conversationId: string; person: ChatPerson; updatedAt: string }>; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const mine = await supabase
    .from('chat_participants')
    .select('conversation_id,conversation:conversation_id!inner(conversation_type,updated_at)')
    .eq('profile_id', profileId)
    .is('left_at', null)
    .eq('conversation.conversation_type', 'direct');
  if (mine.error) return { data: [], error: mine.error };
  const updated = new Map<string, string>();
  for (const row of (mine.data || []) as Array<Record<string, any>>) {
    const conversation = Array.isArray(row.conversation) ? row.conversation[0] : row.conversation;
    updated.set(String(row.conversation_id), String(conversation?.updated_at || ''));
  }
  const ids = [...updated.keys()];
  const partners: Array<{ conversationId: string; person: ChatPerson; updatedAt: string }> = [];
  // Chunked: a long .in() list overflows the request URL.
  for (let start = 0; start < ids.length; start += 150) {
    const others = await supabase
      .from('chat_participants')
      .select(`conversation_id,profile:profile_id(${CHAT_PERSON_COLUMNS})`)
      .in('conversation_id', ids.slice(start, start + 150))
      .neq('profile_id', profileId)
      .is('left_at', null);
    if (others.error) return { data: [], error: others.error };
    for (const row of (others.data || []) as Array<Record<string, any>>) {
      const profile = Array.isArray(row.profile) ? row.profile[0] : row.profile;
      if (!profile?.id) continue;
      partners.push({ conversationId: String(row.conversation_id), person: toChatPerson(profile), updatedAt: updated.get(String(row.conversation_id)) || '' });
    }
  }
  partners.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  // One row per person: older duplicate direct chats with the same person keep the newest.
  const seen = new Set<string>();
  const unique = partners.filter((entry) => !seen.has(entry.person.id) && Boolean(seen.add(entry.person.id)));
  return { data: unique.slice(0, options?.limit ?? 6), error: null };
}

export async function uploadChatAttachment(
  params: { bucket: string; path: string; file: File },
  options?: ClientOption,
): Promise<StorageError | null> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase.storage.from(params.bucket).upload(params.path, params.file, {
    cacheControl: '1800',
    upsert: false,
  });
  return error ?? null;
}

export async function createChatSignedUrl(
  params: { bucket: string; path: string; expiresIn: number },
  options?: ClientOption,
): Promise<{ url: string | null; error: StorageError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase.storage.from(params.bucket).createSignedUrl(params.path, params.expiresIn);
  return { url: data?.signedUrl ?? null, error: error ?? null };
}

export async function fetchChatProfile(
  profileId: string,
  options?: ClientOption,
): Promise<{ data: ChatProfile | null; error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { data, error } = await supabase
    .from('user_profiles')
    .select('id,full_name,first_name,last_name,email,headshot_url')
    .eq('id', profileId)
    .maybeSingle();
  return { data: (data as ChatProfile | null) ?? null, error: error ?? null };
}

export async function pinMessage(
  messageId: string,
  profileId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('quote_chat_messages')
    .update({ pinned_at: new Date().toISOString(), pinned_by: profileId })
    .eq('id', messageId);
  return { error };
}

export async function unpinMessage(
  messageId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('quote_chat_messages')
    .update({ pinned_at: null, pinned_by: null })
    .eq('id', messageId);
  return { error };
}

export async function pinConversation(
  conversationId: string,
  profileId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('chat_conversations')
    .update({ pinned_at: new Date().toISOString(), pinned_by: profileId })
    .eq('conversation_id', conversationId);
  return { error };
}

export async function unpinConversation(
  conversationId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('chat_conversations')
    .update({ pinned_at: null, pinned_by: null })
    .eq('conversation_id', conversationId);
  return { error };
}

export async function muteConversation(
  conversationId: string,
  profileId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('chat_participants')
    .update({ muted_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('profile_id', profileId)
    .is('left_at', null);
  return { error };
}

export async function unmuteConversation(
  conversationId: string,
  profileId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('chat_participants')
    .update({ muted_at: null })
    .eq('conversation_id', conversationId)
    .eq('profile_id', profileId);
  return { error };
}

/**
 * Lightweight HEAD query that counts unread messages in a single conversation.
 * Compares against the localStorage `lastReadAt` timestamp so no full rows
 * are transferred.
 *
 * When no `lastReadAt` exists we look for server-side read receipts in
 * `quote_chat_message_reads`.  If those exist we seed localStorage from
 * the newest receipt's message `created_at`.  Otherwise we fall back to
 * `seedFallback` (the conversation's `last_message_at` passed by the
 * caller) or NOW, whichever is available.  This avoids both:
 *   - phantom badges (counting every historical message as unread), and
 *   - hidden messages (seeding to NOW when the user was offline).
 */
export async function countUnreadForConversation(conversationId: string, profileId: string, options?: ClientOption & { seedFallback?: string | null }): Promise<number> {
  const { data, error } = await fetchConversations(profileId, options);
  if (error) throw error;
  return Number(data.find(row => row.conversation_id === conversationId)?.unread_count ?? 0);
}

export async function leaveConversation(
  conversationId: string,
  profileId: string,
  options?: ClientOption,
): Promise<{ error: PostgrestError | null }> {
  const supabase = options?.supabase ?? getSupabaseClient();
  const { error } = await supabase
    .from('chat_participants')
    .update({ left_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('profile_id', profileId)
    .is('left_at', null);
  return { error };
}
